import React, { useState, useRef, useCallback, useEffect } from 'react';
import { cn } from '../../utils/cn';
import { useAnimationStore } from '../../store/animationStore';
import {
  Keyframe,
  PropertyTrack,
  EasingType,
  EASING_PRESETS,
} from '../../../core/types/animation.types';

interface KeyframeValueEditorProps {
  keyframe: Keyframe;
  track: PropertyTrack;
  animationId: string;
  position: { x: number; y: number };
  onClose: () => void;
}

const KeyframeValueEditor: React.FC<KeyframeValueEditorProps> = ({
  keyframe,
  track,
  animationId,
  position,
  onClose,
}) => {
  const { updateKeyframe, setKeyframeEasing } = useAnimationStore();
  const [value, setValue] = useState(
    typeof keyframe.value === 'number' ? keyframe.value.toString() : keyframe.value
  );
  const [easing, setEasing] = useState<EasingType>(keyframe.easing);
  const editorRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (editorRef.current && !editorRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleValueBlur = () => {
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) {
      updateKeyframe(animationId, track.id, keyframe.id, { value: parsed });
    }
  };

  const handleValueKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleValueBlur();
      onClose();
    }
  };

  const handleEasingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newEasing = e.target.value as EasingType;
    setEasing(newEasing);
    setKeyframeEasing(animationId, track.id, keyframe.id, newEasing);
  };

  // Group easing presets by category
  const easingCategories = EASING_PRESETS.reduce((acc, preset) => {
    if (!acc[preset.category]) {
      acc[preset.category] = [];
    }
    acc[preset.category].push(preset);
    return acc;
  }, {} as Record<string, typeof EASING_PRESETS>);

  return (
    <div
      ref={editorRef}
      className={cn(
        'absolute z-50 bg-zinc-800 border border-zinc-600 rounded-lg shadow-xl',
        'p-3 min-w-[200px]'
      )}
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -100%) translateY(-8px)',
      }}
    >
      {/* Arrow pointer */}
      <div
        className="absolute left-1/2 bottom-0 w-3 h-3 bg-zinc-800 border-r border-b border-zinc-600 transform rotate-45 -translate-x-1/2 translate-y-1/2"
      />

      <div className="space-y-3">
        {/* Time display */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-400">Time</span>
          <span className="text-zinc-200 font-mono">{keyframe.time.toFixed(3)}s</span>
        </div>

        {/* Value input */}
        <div className="space-y-1">
          <label className="text-xs text-zinc-400">Value</label>
          <input
            type="text"
            value={value}
            onChange={handleValueChange}
            onBlur={handleValueBlur}
            onKeyDown={handleValueKeyDown}
            className={cn(
              'w-full px-2 py-1.5 text-xs bg-zinc-900 border border-zinc-700 rounded',
              'text-zinc-100 focus:outline-none focus:border-blue-500'
            )}
            autoFocus
          />
        </div>

        {/* Easing select */}
        <div className="space-y-1">
          <label className="text-xs text-zinc-400">Easing</label>
          <select
            value={easing}
            onChange={handleEasingChange}
            className={cn(
              'w-full px-2 py-1.5 text-xs bg-zinc-900 border border-zinc-700 rounded',
              'text-zinc-100 focus:outline-none focus:border-blue-500',
              'cursor-pointer'
            )}
          >
            {Object.entries(easingCategories).map(([category, presets]) => (
              <optgroup key={category} label={category}>
                {presets.map((preset) => (
                  <option key={preset.type} value={preset.type}>
                    {preset.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

interface KeyframeDiamondProps {
  keyframe: Keyframe;
  track: PropertyTrack;
  animationId: string;
  pixelsPerSecond: number;
  scrollLeft: number;
  trackColor: string;
}

export const KeyframeDiamond: React.FC<KeyframeDiamondProps> = ({
  keyframe,
  track,
  animationId,
  pixelsPerSecond,
  scrollLeft,
  trackColor,
}) => {
  const { selectedKeyframeIds, selectKeyframe, moveKeyframe } = useAnimationStore();
  const [isDragging, setIsDragging] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editorPosition, setEditorPosition] = useState({ x: 0, y: 0 });
  const diamondRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ x: 0, time: 0 });

  const isSelected = selectedKeyframeIds.includes(keyframe.id);
  const xPosition = keyframe.time * pixelsPerSecond - scrollLeft;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (track.locked) return;
      e.preventDefault();
      e.stopPropagation();

      // Select keyframe
      const addToSelection = e.shiftKey || e.ctrlKey || e.metaKey;
      selectKeyframe(keyframe.id, addToSelection);

      // Start dragging
      setIsDragging(true);
      dragStartRef.current = { x: e.clientX, time: keyframe.time };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - dragStartRef.current.x;
        const deltaTime = deltaX / pixelsPerSecond;
        const newTime = Math.max(0, dragStartRef.current.time + deltaTime);
        moveKeyframe(animationId, track.id, keyframe.id, newTime);
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    },
    [track.locked, track.id, keyframe.id, keyframe.time, animationId, pixelsPerSecond, selectKeyframe, moveKeyframe]
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (track.locked) return;
      e.preventDefault();
      e.stopPropagation();

      const rect = diamondRef.current?.getBoundingClientRect();
      if (rect) {
        setEditorPosition({
          x: rect.left + rect.width / 2,
          y: rect.top,
        });
        setShowEditor(true);
      }
    },
    [track.locked]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const addToSelection = e.shiftKey || e.ctrlKey || e.metaKey;
      selectKeyframe(keyframe.id, addToSelection);
    },
    [keyframe.id, selectKeyframe]
  );

  const formatValue = (value: number | string): string => {
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    return value;
  };

  return (
    <>
      <div
        ref={diamondRef}
        className={cn(
          'absolute top-1/2 -translate-y-1/2 w-3 h-3 transform rotate-45 cursor-pointer',
          'border-2 transition-all duration-75',
          isSelected ? 'bg-white border-white scale-110' : 'border-current',
          isDragging && 'scale-125',
          track.locked && 'cursor-not-allowed opacity-50'
        )}
        style={{
          left: xPosition - 6, // Center the diamond (half of 12px width)
          borderColor: isSelected ? 'white' : trackColor,
          backgroundColor: isSelected ? 'white' : trackColor,
        }}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        onClick={handleClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      />

      {/* Tooltip */}
      {showTooltip && !isDragging && !showEditor && (
        <div
          className={cn(
            'absolute z-40 px-2 py-1 bg-zinc-900 border border-zinc-700 rounded shadow-lg',
            'text-xs text-zinc-200 whitespace-nowrap pointer-events-none'
          )}
          style={{
            left: xPosition,
            top: -28,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="font-mono">
            {formatValue(keyframe.value)} @ {keyframe.time.toFixed(2)}s
          </div>
          <div className="text-zinc-400 text-[10px]">{keyframe.easing}</div>
        </div>
      )}

      {/* Value editor popup */}
      {showEditor && (
        <KeyframeValueEditor
          keyframe={keyframe}
          track={track}
          animationId={animationId}
          position={editorPosition}
          onClose={() => setShowEditor(false)}
        />
      )}
    </>
  );
};

export default KeyframeDiamond;
